import { getToken, clearToken } from '../utils/authToken';
/**
 * DiagramGeneratorModal - Version with external open control
 * Wraps DiagramGenerator functionality with isOpen/onClose props
 */
import { useState, useEffect } from 'react';
import { API_URL, API } from '../config';
import { createPortal } from 'react-dom';
import { GitBranch, Loader2, X, Sparkles, Copy, Check, SendHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { navigateToPremium } from '../utils/navigateToPremium';


const DiagramGeneratorModal = ({ isOpen, onClose, conversationId, onDiagramGenerated }) => {
  const { language } = useLanguage();
  const [description, setDescription] = useState('');
  const [diagramType, setDiagramType] = useState('flowchart');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);

  const DIAGRAM_TYPES = [
    { value: 'flowchart', label: language === 'es' ? 'Diagrama de Flujo' : 'Flowchart', icon: '' },
    { value: 'sequence', label: language === 'es' ? 'Diagrama de Secuencia' : 'Sequence Diagram', icon: '' },
    { value: 'class', label: language === 'es' ? 'Diagrama de Clases' : 'Class Diagram', icon: '' },
    { value: 'er', label: language === 'es' ? 'Diagrama ER' : 'ER Diagram', icon: '' },
    { value: 'state', label: language === 'es' ? 'Diagrama de Estados' : 'State Diagram', icon: '' },
    { value: 'gantt', label: language === 'es' ? 'Diagrama de Gantt' : 'Gantt Chart', icon: '' },
    { value: 'mindmap', label: language === 'es' ? 'Mapa Mental' : 'Mind Map', icon: '' },
    { value: 'pie', label: language === 'es' ? 'Grafico Circular' : 'Pie Chart', icon: '' },
    { value: 'architecture', label: language === 'es' ? 'Arquitectura' : 'Architecture', icon: '' },
    { value: 'api', label: language === 'es' ? 'Flujo de API' : 'API Flow', icon: '' },
  ];

  const txt = {
    es: {
      title: 'Generar Diagrama',
      diagramType: 'Tipo de diagrama:',
      describe: 'Describe el diagrama',
      createBtn: 'CREAR DIAGRAMA',
      generating: 'Generando...',
      generated: 'Diagrama generado!',
      insertChat: 'Insertar en el Chat',
      inserted: 'Diagrama insertado en el chat',
      codeGenerated: 'Codigo Mermaid generado',
      copied: 'Codigo copiado',
      errorDescribe: 'Describe que diagrama necesitas',
      errorCopy: 'Error al copiar',
    },
    en: {
      title: 'Generate Diagram',
      diagramType: 'Diagram type:',
      describe: 'Describe the diagram',
      createBtn: 'CREATE DIAGRAM',
      generating: 'Generating...',
      generated: 'Diagram generated!',
      insertChat: 'Insert to Chat',
      inserted: 'Diagram inserted to chat',
      codeGenerated: 'Mermaid code generated',
      copied: 'Code copied',
      errorDescribe: 'Describe what diagram you need',
      errorCopy: 'Error copying',
    }
  };

  const loc = txt[language] || txt.es;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGeneratedCode('');
      setDescription('');
      setCopied(false);
      setSent(false);
      setIsSending(false);
    }
  }, [isOpen]);

  const handleSendToWorkspace = async () => {
    if (!generatedCode || isSending) return;
    setIsSending(true);
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const res = await axios.post(`${API}/workspace/auto`, {
        content: generatedCode,
        source: 'diagram',
        title: description?.trim() ? description.trim().slice(0, 60) : null,
      }, { headers });
      const moduleId = res.data?.module_id;
      const detected = res.data?.detected_type || 'diagram';
      const moduleName = res.data?.name || 'Diagrama';
      // Notificar a InternalWorkspace para abrir el editor con el módulo
      window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
        detail: { moduleId, name: moduleName, detectedType: detected },
      }));
      setSent(true);
      toast.success(language === 'es'
        ? `Enviado al Área de Trabajo (${detected})`
        : `Sent to Workspace (${detected})`);
      setTimeout(() => onClose && onClose(), 1200);
    } catch (error) {
      const status = error?.response?.status;
      const detailRaw = error?.response?.data?.detail;
      const isPremiumLock = status === 403 && (
        (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
        (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
      );
      if (isPremiumLock) {
        toast.error(
          language === 'es'
            ? 'Enviar al Área de Trabajo es exclusivo Premium. Actualiza tu plan.'
            : 'Send to Workspace is Premium-only. Upgrade your plan.',
          {
            duration: 6000,
            action: {
              label: language === 'es' ? 'Actualizar' : 'Upgrade',
              onClick: navigateToPremium,
            },
          }
        );
      } else {
        const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
        toast.error(language === 'es' ? `Error: ${detail}` : `Error: ${detail}`);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error(loc.errorDescribe);
      return;
    }

    setIsGenerating(true);
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      
      const response = await axios.post(`${API}/generate-diagram`, {
        description,
        diagram_type: diagramType,
        conversation_id: conversationId
      }, { headers });

      if (response.data.mermaid_code) {
        setGeneratedCode(response.data.mermaid_code);
        toast.success(loc.codeGenerated);
      }
    } catch (error) {
      console.error('Error generating diagram:', error);
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedCode) return;

    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = generatedCode;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    let success = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(generatedCode);
        success = true;
      }
    } catch (clipErr) {
    }

    if (!success) {
      success = copyWithTextarea();
    }

    if (success) {
      setCopied(true);
      toast.success(loc.copied);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(loc.errorCopy);
    }
  };

  const handleInsertToChat = () => {
    if (!generatedCode || !onDiagramGenerated) return;
    onDiagramGenerated({
      type: 'insert',
      mermaid_code: generatedCode,
      description: description
    });
    toast.success(loc.inserted);
    onClose();
  };

  const selectedType = DIAGRAM_TYPES.find(t => t.value === diagramType);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl my-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg">
              <GitBranch className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-semibold text-white text-sm">{loc.title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors" data-testid="close-diagram-modal-btn">
            <X className="w-4 h-4 text-zinc-400 hover:text-white" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">{loc.diagramType}</label>
            <select
              value={diagramType}
              onChange={(e) => setDiagramType(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid="diagram-type-select"
            >
              {DIAGRAM_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">{loc.describe}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={language === 'es' 
                ? 'Ej: Flujo de autenticacion de usuario con login, registro y recuperacion de password...'
                : 'E.g.: User authentication flow with login, signup and password recovery...'}
              rows={3}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 resize-none"
              data-testid="diagram-description-input"
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !description.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:from-zinc-700 disabled:to-zinc-700 text-white font-bold rounded-lg transition-all disabled:cursor-not-allowed shadow-lg shadow-cyan-500/25"
            data-testid="generate-diagram-execute-btn"
          >
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin" />{loc.generating}</>
            ) : (
              <><Sparkles className="w-5 h-5" />{loc.createBtn}</>
            )}
          </button>

          {generatedCode && (
            <div className="mt-3 space-y-2">
              <div className="relative">
                <pre className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-cyan-300 overflow-x-auto max-h-40 font-mono">
                  {generatedCode}
                </pre>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <button 
                  onClick={handleCopy} 
                  className="flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-2 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-medium rounded-lg transition-colors border border-zinc-600"
                  data-testid="copy-diagram-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? loc.copied : 'Copiar'}
                </button>
                {/* Enviar al Area de Trabajo: crea un modulo interno con el
                    diagrama Mermaid renderizado, titulo inteligente y tags. */}
                <button
                  onClick={handleSendToWorkspace}
                  disabled={isSending || sent}
                  className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-2 py-2 ${
                    sent
                      ? 'bg-emerald-700 text-emerald-200'
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  } text-xs font-medium rounded-lg transition-colors disabled:opacity-70 disabled:cursor-wait`}
                  data-testid="send-diagram-to-workspace-btn"
                  title={language === 'es' ? 'Enviar al Area de Trabajo' : 'Send to Workspace'}
                >
                  {isSending ? (
                    <span className="flex items-center gap-1" aria-label="processing">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-200 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-200 animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-200 animate-bounce" style={{ animationDelay: '240ms' }} />
                    </span>
                  ) : sent ? (
                    <><Check className="w-3.5 h-3.5" />{language === 'es' ? 'Enviado' : 'Sent'}</>
                  ) : (
                    <><SendHorizontal className="w-3.5 h-3.5" />{language === 'es' ? 'Enviar' : 'Send'}</>
                  )}
                </button>
                {onDiagramGenerated && (
                  <button 
                    onClick={handleInsertToChat} 
                    className="flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-2 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-colors"
                    data-testid="insert-diagram-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5" />{loc.insertChat}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DiagramGeneratorModal;
